import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingCart, Package, Receipt, BarChart3, LogOut, Menu, AlertTriangle, Store, Settings, Users, BookOpen, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { StoreProvider, useStore } from "@/context/StoreContext";
import { MANAGERS, ALL_ROLES, ROLE_LABEL, ROLE_TONE, can } from "@/lib/roles";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", roles: MANAGERS },
  { to: "/kasir", label: "Kasir", icon: ShoppingCart, id: "kasir", roles: ALL_ROLES },
  { to: "/produk", label: "Produk & Stok", icon: Package, id: "produk", roles: MANAGERS },
  { to: "/riwayat", label: "Riwayat Transaksi", icon: Receipt, id: "riwayat", roles: ALL_ROLES },
  { to: "/laporan", label: "Laporan Penjualan", icon: BarChart3, id: "laporan", roles: MANAGERS },
  { to: "/pengaturan", label: "Pengaturan Toko", icon: Settings, id: "pengaturan", roles: MANAGERS },
  { to: "/users", label: "Kelola User", icon: Users, id: "users", roles: ["owner"] },
  { to: "/menu", label: "Menu Pelanggan", icon: BookOpen, id: "menu", roles: ["kasir"], external: true },
];

const NAV_CLASS = "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200";

const NavList = ({ onNavigate, prefix }) => {
  const { user } = useAuth();
  return (
  <nav className="flex flex-col gap-1 px-3">
    {NAV.filter((n) => can(user, n.roles)).map(({ to, label, icon: Icon, id, external }) => external ? (
      <a key={to} href={to} target="_blank" rel="noopener noreferrer" onClick={onNavigate} data-testid={`${prefix}nav-${id}-link`}
        className={`${NAV_CLASS} text-slate-300 hover:bg-white/5 hover:text-white`}>
        <Icon className="h-4 w-4" />
        {label}
        <ExternalLink className="ml-auto h-3.5 w-3.5 opacity-60" />
      </a>
    ) : (
      <NavLink
        key={to}
        to={to}
        end={to === "/"}
        onClick={onNavigate}
        data-testid={`${prefix}nav-${id}-link`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
            isActive ? "bg-emerald-500/15 text-emerald-300" : "text-slate-300 hover:bg-white/5 hover:text-white"
          }`
        }
      >
        <Icon className="h-4 w-4" />
        {label}
      </NavLink>
    ))}
  </nav>
  );
};

const Brand = () => {
  const { store } = useStore();
  return (
  <div className="flex items-center gap-3 px-6 py-6">
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500 text-white">
      <Store className="h-5 w-5" />
    </div>
    <div>
      <p className="font-display text-base font-extrabold text-white leading-tight">KasirUMKM</p>
      <p className="text-xs text-slate-400" data-testid="sidebar-store-name">{store.name}</p>
    </div>
  </div>
  );
};

export const Layout = () => (
  <StoreProvider>
    <Shell />
  </StoreProvider>
);

const Shell = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [lowCount, setLowCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const isManager = can(user, MANAGERS);

  useEffect(() => {
    if (isManager) api.get("/products/low-stock").then((r) => setLowCount(r.data.length)).catch(() => {});
  }, [location.pathname, isManager]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-slate-900 lg:flex">
        <Brand />
        <NavList prefix="" />
        <div className="mt-auto p-4">
          <Button variant="ghost" onClick={handleLogout} data-testid="logout-button" className="w-full justify-start gap-3 text-slate-300 hover:bg-white/5 hover:text-white">
            <LogOut className="h-4 w-4" /> Keluar
          </Button>
        </div>
      </aside>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 border-none bg-slate-900 p-0">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <Brand />
          <NavList prefix="mobile-" onNavigate={() => setOpen(false)} />
          <div className="p-4">
            <Button variant="ghost" onClick={handleLogout} data-testid="mobile-logout-button" className="w-full justify-start gap-3 text-slate-300 hover:bg-white/5 hover:text-white">
              <LogOut className="h-4 w-4" /> Keluar
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <div className="lg:pl-64">
        <header className="no-print sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white/85 px-4 py-3 backdrop-blur-md md:px-8">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <p className="font-display text-sm font-semibold text-slate-700">{NAV.find((n) => n.to === location.pathname)?.label}</p>
          <div className="ml-auto flex items-center gap-3">
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${ROLE_TONE[user?.role] || ""}`} data-testid="current-user-role">{ROLE_LABEL[user?.role]}</span>
            {isManager && <button
              onClick={() => navigate("/produk?filter=low")}
              data-testid="low-stock-alert-badge"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                lowCount ? "bg-amber-100 text-amber-800 hover:bg-amber-200" : "bg-slate-100 text-slate-500"
              }`}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              <span data-testid="low-stock-count">{lowCount}</span> stok menipis
            </button>}
            <div className="hidden text-right sm:block">
              <p className="text-sm font-semibold text-slate-800" data-testid="current-user-name">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
        </header>
        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
